import { useTranslation } from 'react-i18next';

import { ImagePlaceholder } from '@/components/ImagePlaceholder';
import { resolvePhotoPosition, type Advisor } from '@/lib/db/admin/advisors';

export type DealAdvisorCardProps = {
  /** Advisor to display — the deal's assigned advisor or the table default.
   * The parent only renders this card when one exists (strict, no fallback). */
  advisor: Advisor;
};

// Advisor contact card of the deal page (M13). Rendered in the desktop
// sidebar and inline in the main column on mobile. Data comes exclusively
// from the `advisors` table: the deal's assigned advisor, else the
// `is_default` advisor — never a hardcoded identity. Reuses the
// `deal-sidebar__advisor*` classes (flat, not scoped to the sidebar).
export function DealAdvisorCard({ advisor }: DealAdvisorCardProps) {
  const { t } = useTranslation();
  return (
    <div className="deal-sidebar__advisor">
      <div className="deal-sidebar__kicker" style={{ color: 'var(--ddm-bronze)' }}>
        {t('deal.sidebar.advisorKicker')}
      </div>
      <div className="deal-sidebar__advisor-row">
        <div className="deal-sidebar__advisor-avatar">
          {advisor.photo_url ? (
            <img
              src={advisor.photo_url}
              alt={`${advisor.first_name} ${advisor.last_name}`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: resolvePhotoPosition(advisor.photo_position),
                borderRadius: '50%',
              }}
            />
          ) : (
            <ImagePlaceholder label={t('deal.sidebar.advisorAvatar')} />
          )}
        </div>
        <div>
          <div className="deal-sidebar__advisor-name">
            {advisor.first_name} {advisor.last_name}
          </div>
          <div className="deal-sidebar__advisor-role">{advisor.title}</div>
        </div>
      </div>
      {advisor.email && (
        <a className="deal-sidebar__advisor-email" href={`mailto:${advisor.email}`}>
          {advisor.email}
        </a>
      )}
    </div>
  );
}
